/**
 * HTML sanitisation helper using DOMPurify.
 *
 * Used wherever raw HTML from external sources (email bodies, signatures)
 * is rendered via dangerouslySetInnerHTML to prevent XSS attacks.
 *
 * Config:
 *  - Allows standard rich-text formatting tags and inline styles
 *  - Strips <script>, event handlers (onclick, onerror…), javascript: URIs
 *  - Preserves <img> with http/https src (blocks data: URIs to prevent tracking pixels leaking data)
 *  - Keeps <a href> links but forces target="_blank" and rel="noopener noreferrer"
 */

let DOMPurify = null;

function getPurify() {
  if (!DOMPurify) {
    // Lazy import — avoids SSR issues and keeps the initial bundle lean
    DOMPurify = require('dompurify');
    // If the module uses a default export (ESM interop)
    if (DOMPurify.default) DOMPurify = DOMPurify.default;
  }
  return DOMPurify;
}

const ALLOWED_TAGS = [
  'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'cite', 'code', 'col',
  'colgroup', 'dd', 'del', 'details', 'dfn', 'div', 'dl', 'dt', 'em',
  'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr',
  'i', 'img', 'ins', 'kbd', 'li', 'mark', 'ol', 'p', 'pre', 'q', 's',
  'samp', 'small', 'span', 'strong', 'sub', 'summary', 'sup', 'table',
  'tbody', 'td', 'tfoot', 'th', 'thead', 'time', 'tr', 'u', 'ul', 'var',
  'font', 'center',
];

const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'width', 'height', 'style', 'class',
  'align', 'valign', 'border', 'cellpadding', 'cellspacing', 'colspan',
  'rowspan', 'color', 'bgcolor', 'size', 'face', 'target', 'rel', 'type',
];

/**
 * Sanitise an HTML string for safe rendering.
 * @param {string} html - Raw HTML (e.g. email body or signature content).
 * @returns {string} Sanitised HTML safe for dangerouslySetInnerHTML.
 */
export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') return '';
  try {
    const purify = getPurify();
    const clean = purify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      // Force all links to open in a new tab safely
      ADD_ATTR: ['target'],
    });
    // Post-process: ensure every <a> has rel="noopener noreferrer" and target="_blank"
    return clean.replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer" ');
  } catch (err) {
    console.error('sanitizeHtml error:', err);
    return '';
  }
}
